import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useCompanies as useCompaniesService } from 'services/companies';
import { useUserSelector } from 'hooks/useUser';
import { useLanguage } from '@ui/hooks/useLanguage';

const CompanyContext = React.createContext(null);

export function CompanyContextProvider({ children }) {
  const {
    data: company = {},
    refetch: reloadCompany,
    isLoading: isCompanyLoading
  } = useCompaniesService({ enabled: true });
  const { user } = useUserSelector();
  const [isMortgageApplicationURLInClipboard, setIsMortgageApplicationURLInClipboard] =
    useState(false);
  const [isRepaymentCalculatorInClipboard, setIsRepaymentCalculatorInClipboard] = useState(false);
  const { language } = useLanguage();
  const [shareLanguage, setShareLanguage] = useState(language?.lang || 'en');

  const isSuperAdmin = useMemo(() => {
    return user?.user_id === company?.owner_user_id;
  }, [user, company]);

  const mortgageApplicationReferralLink = useMemo(
    () =>
      `${process.env.REACT_APP_UPSCORE_URL}/p/${company?.short_id}/${user?.own_referral_id}?lang=${shareLanguage}`,
    [company?.short_id, user?.own_referral_id, shareLanguage]
  );

  const repaymentCalculatorShareLink = useMemo(
    () =>
      `
      <iframe src="${process.env.REACT_APP_UPSCORE_URL}/calculators/repayments?pcid=${company?.short_id}&aid=${user?.own_referral_id}&lang=${shareLanguage}" width="100%" height="820" style="border:none;transition:height 0.3s ease;"></iframe>
      <script>
        window.addEventListener('message', (event) => {
          if (event.data.type === 'iframeHeight') {
            const iframe = document.querySelector('iframe');
            if (iframe) {
              iframe.style.height = event.data.height + 'px';
            }
          }
        });
      </script>
      `,
    [company?.short_id, user?.own_referral_id, shareLanguage]
  );

  const copyToClipboardRepaymentCalculatorShareLink = useCallback(() => {
    navigator.clipboard.writeText(repaymentCalculatorShareLink);
    setIsRepaymentCalculatorInClipboard(true);

    setTimeout(() => {
      setIsRepaymentCalculatorInClipboard(false);
    }, 1000);
  }, [repaymentCalculatorShareLink, setIsRepaymentCalculatorInClipboard]);

  const copyToClipboardMortgageApplicationUrl = useCallback(() => {
    navigator.clipboard.writeText(mortgageApplicationReferralLink);
    setIsMortgageApplicationURLInClipboard(true);

    setTimeout(() => {
      setIsMortgageApplicationURLInClipboard(false);
    }, 1000);
  }, [mortgageApplicationReferralLink, setIsMortgageApplicationURLInClipboard]);

  useEffect(() => {
    if (language?.lang) {
      setShareLanguage(language.lang);
    }
  }, [language, setShareLanguage]);

  return (
    <CompanyContext.Provider
      value={{
        company,
        reloadCompany,
        isCompanyLoading,
        isSuperAdmin,
        isMortgageApplicationURLInClipboard,
        copyToClipboardMortgageApplicationUrl,
        mortgageApplicationReferralLink,
        shareLanguage,
        setShareLanguage,
        repaymentCalculatorShareLink,
        isRepaymentCalculatorInClipboard,
        copyToClipboardRepaymentCalculatorShareLink
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);

  if (!context) {
    throw new Error('useCompany must be used within a CompanyContextProvider');
  }

  return context;
}
